import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import Navbar from '../components/nav/Navbar';
import RealtimeMetrics from '../components/artist/RealtimeMetrics';
import ArtistActivityFeed from '../components/artist/ArtistActivityFeed';
import { useArtist } from '../hooks/useArtist';
import supabase from '../supabase/supabase';

const { FiBarChart2, FiCalendar, FiTrendingUp, FiUsers, FiDollarSign } = FiIcons;

const AnalyticsDashboard = () => {
  const { artistId } = useParams();
  const { artist } = useArtist(artistId);
  const [timeRange, setTimeRange] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!artistId) return;

    const fetchAnalytics = async () => {
      const { data, error } = await supabase.rpc('get_artist_analytics_enhanced', {
        p_artist_id: artistId,
        p_days_back: timeRange,
      });

      if (!error && data && data.length > 0) {
        setAnalytics(data[0]);
      }
      setLoading(false);
    };

    fetchAnalytics();
  }, [artistId, timeRange]);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink w-[700px] h-[700px] top-[-20%] right-[-15%] animate-caustic" />
        <div className="ambient-orb-coral w-[500px] h-[500px] bottom-[-10%] left-[-10%] animate-caustic" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-24 space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div>
            <span className="text-xs font-black tracking-widest text-brand-pink uppercase">Analytics</span>
            <h1 className="text-5xl font-black uppercase tracking-tighter text-white mt-1">
              {artist?.name || 'Artist'} Analytics
            </h1>
            <p className="text-zinc-500 font-bold text-sm mt-2">
              Performance metrics and fan engagement
            </p>
          </div>
          <div className="flex gap-1 glass-card rounded-xl p-1">
            {[7, 30, 90].map(days => (
              <button
                key={days}
                onClick={() => setTimeRange(days)}
                className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${
                  timeRange === days ? 'bg-white text-black' : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {days}D
              </button>
            ))}
          </div>
        </div>

        {/* Real-time Metrics */}
        <RealtimeMetrics artistId={artistId} />

        {/* Analytics Summary */}
        {!loading && analytics && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="glass-card rounded-2xl p-5 space-y-2">
                <SafeIcon icon={FiUnlock} className="text-2xl text-brand-pink" />
                <p className="text-3xl font-black text-white">{analytics.total_unlocks}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Total Unlocks</p>
              </div>
              <div className="glass-card rounded-2xl p-5 space-y-2">
                <SafeIcon icon={FiDollarSign} className="text-2xl text-green-400" />
                <p className="text-3xl font-black text-white">${analytics.total_revenue?.toFixed(2)}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Total Revenue</p>
              </div>
              <div className="glass-card rounded-2xl p-5 space-y-2">
                <SafeIcon icon={FiUsers} className="text-2xl text-purple-400" />
                <p className="text-3xl font-black text-white">{analytics.total_subscribers}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Active Subscribers</p>
              </div>
              <div className="glass-card rounded-2xl p-5 space-y-2">
                <SafeIcon icon={FiTrendingUp} className="text-2xl text-blue-400" />
                <p className="text-3xl font-black text-white">{analytics.new_followers}</p>
                <p className="text-[10px] font-black uppercase tracking-widest text-zinc-500">New Followers</p>
              </div>
            </div>

            {/* Top Drop */}
            {analytics.top_drop_id && (
              <div className="glass-card rounded-3xl p-6">
                <h3 className="font-black text-lg uppercase tracking-tight text-white mb-4">Top Performing Drop</h3>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-xl bg-zinc-900 flex items-center justify-center">
                    <SafeIcon icon={FiMusic} className="text-2xl text-brand-pink" />
                  </div>
                  <div>
                    <p className="font-black text-lg uppercase tracking-tight text-white">{analytics.top_drop_title}</p>
                    <p className="text-sm font-bold text-zinc-500">{analytics.top_drop_unlocks} unlocks</p>
                  </div>
                </div>
              </div>
            )}

            {/* Activity Feed */}
            <div className="glass-card rounded-3xl p-6">
              <h3 className="font-black text-lg uppercase tracking-tight text-white mb-4 flex items-center gap-2">
                <SafeIcon icon={FiBarChart2} className="text-brand-coral" />
                Recent Activity
              </h3>
              <ArtistActivityFeed artistId={artistId} limit={5} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;